// src/components/nono/NonoEditor.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDate, isPast, isToday, nextDay, parseId } from '../../utils/dateUtils';
import MonthCalendar from '../ui/MonthCalendar';
import UiIcon from '../ui/UiIcon';
import { getDayInfo } from '../crossword/finnishDays';
import { analyze, decodeCells, difficultyText, encodeCells, randomWarmup, STATUS_TEXT } from '../../utils/nonoSolver';
import { withoutLines } from '../../utils/oneBit';
import { sourceFromBlob, sourceFromEmoji, sourceFromSymbol } from '../../utils/nonoRaster';
import { deleteNonoPuzzle, fetchNonoPuzzles, moveNonoPuzzles, saveNonoPuzzle } from '../../services/nonoService';
import NonoGridEditor from './NonoGridEditor';
import NonoTracer from './NonoTracer';
import NonoPixelTracer from './NonoPixelTracer';
import NonoPixelSheet from './NonoPixelSheet';
import OneBitPickerDialog from './OneBitPickerDialog';
import IconPickerDialog from './IconPickerDialog';
import EmojiPickerDialog from '../crossword/EmojiPickerDialog';
import useMediaQuery from '../../hooks/useMediaQuery';

const SIZES = [[5, 5], [10, 10], [10, 15]];
const MIN_SIZE = 1;
const MAX_SIZE = 25;
const REPEAT_DAYS = 365;
const SIZE_KEY = 'nono-main-size';
// Phone width for the grid and clues in the app (390 pt screen minus margins), and the cell size that still taps well.
const PHONE_WIDTH = 358;
const PHONE_HEIGHT = 560;
const PHONE_MIN_CELL = 26;

const emptyGrid = (width, height) => ({ width, height, cells: new Array(width * height).fill(0), title: '' });
const gridFromDoc = (g) => ({ width: g.width, height: g.height, cells: decodeCells(g.cells, g.width * g.height), title: g.title || '' });
const gridToDoc = (g) => ({ width: g.width, height: g.height, cells: encodeCells(g.cells), title: g.title.trim() });
const docOf = (draft) => ({ main: gridToDoc(draft.main) });
const savedDocOf = (p) => (p ? { main: p.main } : null);

const isDirty = (main, saved) => {
  if (!saved) return main.title !== '' || main.cells.includes('1');
  return main.width !== saved.width || main.height !== saved.height || main.cells !== saved.cells || main.title !== (saved.title || '');
};

const lastMainSize = () => {
  try {
    const [w, h] = JSON.parse(localStorage.getItem(SIZE_KEY));
    if (w >= MIN_SIZE && h >= MIN_SIZE) return [w, h];
  } catch { /* none saved */ }
  return [10, 10];
};

const newDraft = () => {
  const [w, h] = lastMainSize();
  return { main: emptyGrid(w, h) };
};

// Same layout math as NonoBoardLayout in the app, to estimate the cell size on a phone.
const phoneCell = (clues, w, h) => {
  const rowUnits = Math.max(0.9, ...clues.rows.map(c => (c.length
    ? c.reduce((a, n) => a + String(n).length, 0) * 0.34 + (c.length - 1) * 0.22 + 0.4
    : 0.74)));
  const colUnits = Math.max(0.9, ...clues.cols.map(c => Math.max(1, c.length) * 0.62 + 0.24));
  return Math.floor(Math.min(PHONE_WIDTH / (w + rowUnits), PHONE_HEIGHT / (h + colUnits), 46));
};

const resized = (grid, width, height) => {
  const cells = new Array(width * height).fill(0);
  for (let r = 0; r < Math.min(height, grid.height); r++) {
    for (let c = 0; c < Math.min(width, grid.width); c++) cells[r * width + c] = grid.cells[r * grid.width + c];
  }
  return { ...grid, width, height, cells };
};

const transformed = (grid, map) => {
  const { width: w, height: h } = grid;
  const cells = new Array(w * h).fill(0);
  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      const from = map(r, c, w, h);
      if (from && from.r >= 0 && from.c >= 0 && from.r < h && from.c < w) cells[r * w + c] = grid.cells[from.r * w + from.c];
    }
  }
  return { ...grid, cells };
};

const cropped = (grid) => {
  const { width: w, height: h, cells } = grid;
  let top = h, bottom = -1, left = w, right = -1;
  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      if (!cells[r * w + c]) continue;
      top = Math.min(top, r); bottom = Math.max(bottom, r);
      left = Math.min(left, c); right = Math.max(right, c);
    }
  }
  if (bottom < 0 || (top === 0 && left === 0 && bottom === h - 1 && right === w - 1)) return grid;
  const out = [];
  for (let r = top; r <= bottom; r++) out.push(...cells.slice(r * w + left, r * w + right + 1));
  return { ...grid, width: right - left + 1, height: bottom - top + 1, cells: out };
};

// Only days after today can be rearranged: today's nono is already live.
const isFuture = (id) => !isPast(id) && !isToday(id);
const idFromInput = (value) => value.split('-').reverse().join('-');
const inputFromId = (id) => id.split('-').reverse().join('-');

const titleFromIcon =(name) => {
  const text = name.replace(/_/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

function NonoThumb({ grid, size = 30 }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !grid) return;
    const cells = typeof grid.cells === 'string' ? decodeCells(grid.cells, grid.width * grid.height) : grid.cells;
    const cell = size / Math.max(grid.width, grid.height);
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.ceil(grid.width * cell * dpr);
    canvas.height = Math.ceil(grid.height * cell * dpr);
    canvas.style.width = `${grid.width * cell}px`;
    canvas.style.height = `${grid.height * cell}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#16181d';
    cells.forEach((v, i) => v && ctx.fillRect((i % grid.width) * cell, Math.floor(i / grid.width) * cell, cell, cell));
  }, [grid, size]);
  return <canvas ref={ref} className="nn-thumb" />;
}

function NonoEditor() {
  const [puzzles, setPuzzles] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [tracer, setTracer] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [picker, setPicker] = useState(null);
  const [lineTool, setLineTool] = useState(null);
  const [mirror, setMirror] = useState({ x: false, y: false });
  const [showErrors, setShowErrors] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [moveDate, setMoveDate] = useState('');
  const [areaWidth, setAreaWidth] = useState(640);
  const [erase, setErase] = useState(false);
  const touch = useMediaQuery('(pointer: coarse)');
  // Below 900px the tracer panel stacks under the canvas, so the canvas gets the full width.
  const stacked = useMediaQuery('(max-width: 900px)');
  const history = useRef({ undo: [], redo: [] });
  const fileInput = useRef(null);
  const areaRef = useRef(null);

  useEffect(() => {
    fetchNonoPuzzles().then(setPuzzles).catch(e => setError(`Lataus epäonnistui: ${e.message}`));
  }, []);

  useEffect(() => {
    const el = areaRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => setAreaWidth(Math.max(280, entry.contentRect.width)));
    observer.observe(el);
    return () => observer.disconnect();
  }, [draft !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = !!selected && isPast(selected);
  const grid = draft ? draft.main : null;
  const dirty = !!draft && isDirty(docOf(draft).main, savedDocOf(puzzles?.[selected])?.main);

  const mainAnalysis = useMemo(() => draft && analyze(draft.main.width, draft.main.height, draft.main.cells), [draft]);
  const analysis = mainAnalysis;

  const repeatOf = useMemo(() => {
    if (!draft || !puzzles || !mainAnalysis?.filled) return null;
    const cells = encodeCells(draft.main.cells);
    const here = parseId(selected);
    return Object.values(puzzles).find(p => p.id !== selected && p.main?.cells === cells &&
      Math.abs(parseId(p.id) - here) / 86400000 <= REPEAT_DAYS)?.id || null;
  }, [draft, puzzles, selected, mainAnalysis]);

  const resetHistory = useCallback(() => {
    history.current = { undo: [], redo: [] };
  }, []);

  const select = useCallback((id) => {
    if (dirty && !window.confirm('Tallentamattomat muutokset katoavat. Jatketaanko?')) return;
    const p = puzzles[id];
    setSelected(id);
    setDraft(p ? { main: gridFromDoc(p.main) } : newDraft());
    setTracer(null);
    setError(null);
    resetHistory();
    const date = parseId(id);
    setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  }, [dirty, puzzles, resetHistory]);

  const record = useCallback(() => {
    const stack = history.current;
    stack.undo.push(draft.main);
    if (stack.undo.length > 200) stack.undo.shift();
    stack.redo = [];
  }, [draft]);

  const setGrid = useCallback((next, { recordHistory = true } = {}) => {
    if (locked) return;
    if (recordHistory) record();
    setDraft(prev => ({ ...prev, main: next }));
  }, [locked, record]);

  const updateActive = (fn) => grid && setGrid(fn(grid));

  const undo = useCallback((redo = false) => {
    if (!draft || locked) return;
    const stack = history.current;
    const from = redo ? stack.redo : stack.undo;
    if (!from.length) return;
    (redo ? stack.undo : stack.redo).push(draft.main);
    const prev = from.pop();
    setDraft(d => ({ ...d, main: prev }));
  }, [draft, locked]);

  const resize = (width, height) => {
    const w = Math.max(MIN_SIZE, Math.min(MAX_SIZE, width || MIN_SIZE));
    const h = Math.max(MIN_SIZE, Math.min(MAX_SIZE, height || MIN_SIZE));
    try { localStorage.setItem(SIZE_KEY, JSON.stringify([w, h])); } catch { /* storage unavailable */ }
    updateActive(g => resized(g, w, h));
  };

  const shift = (dr, dc) => updateActive(g => transformed(g, (r, c) => ({ r: r - dr, c: c - dc })));

  const openTracer = async (load, opts = {}) => {
    if (locked || !grid) return;
    setPicker(null);
    try {
      setTracer({ source: await load(), autoContrast: !!opts.autoContrast, fillable: !!opts.fillable });
      if (opts.title && !draft.main.title.trim()) {
        setDraft(prev => ({ ...prev, main: { ...prev.main, title: opts.title } }));
      }
    } catch (e) {
      setError(`Kuvan avaus epäonnistui: ${e.message}`);
    }
  };

  const openSheet = () => {
    if (locked || !grid) return;
    setPicker(null);
    setLineTool(null);
    setTracer(null);
    setSheet({ file: null });
  };

  const openPixelIcon = (bitmap) => {
    if (locked || !grid) return;
    setPicker(null);
    setLineTool(null);
    setSheet(null);
    setTracer({ bitmap });
  };

  const deleteLine = (kind, index) => {
    updateActive(g => withoutLines(g, kind, index));
  };

  const openImage = (blob) => openTracer(() => sourceFromBlob(blob), { autoContrast: true });

  const save = useCallback(async (advance) => {
    if (!draft || !selected || locked || saving) return;
    if (!mainAnalysis.filled) {
      setError('Päivän nonossa ei ole yhtään täytettyä ruutua');
      return;
    }
    const issues = [];
    if (mainAnalysis.status !== 'logic') issues.push(`Päivän nono: ${STATUS_TEXT[mainAnalysis.status]}`);
    if (!draft.main.title.trim()) issues.push('Kuvalla ei ole nimeä');
    if (repeatOf) issues.push(`Sama kuva on jo päivänä ${repeatOf}`);
    if (issues.length && !window.confirm(`${issues.join('\n')}\n\nTallennetaanko silti?`)) return;
    if (isToday(selected) && puzzles[selected] && !window.confirm('Tämän päivän nono on jo julkaistu. Tallennetaanko silti?')) return;

    setSaving(true);
    try {
      const doc = docOf(draft);
      const puzzle = await saveNonoPuzzle({ id: selected, main: doc.main });
      const next = { ...puzzles, [selected]: puzzle };
      setPuzzles(next);
      setError(null);
      if (advance) {
        let id = nextDay(selected);
        while (next[id]) id = nextDay(id);
        setSelected(id);
        setDraft(newDraft());
        setTracer(null);
        resetHistory();
        const date = parseId(id);
        setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      }
    } catch (e) {
      setError(`Tallennus epäonnistui: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }, [draft, selected, locked, saving, mainAnalysis, repeatOf, puzzles, resetHistory]);

  // moves is { oldId: newId }; the selected day follows its puzzle, or reloads if another one lands on it.
  const applyMoves = async (moves, { stay = false } = {}) => {
    const touched = selected && (selected in moves || Object.values(moves).includes(selected));
    if (touched && dirty && !window.confirm('Tallentamattomat muutokset katoavat. Jatketaanko?')) return;
    try {
      const next = await moveNonoPuzzles(puzzles, moves);
      setPuzzles(next);
      setError(null);
      if (touched) {
        const id = stay ? selected : moves[selected] || selected;
        setSelected(id);
        setDraft(next[id] ? { main: gridFromDoc(next[id].main) } : newDraft());
        resetHistory();
        const date = parseId(id);
        setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      }
    } catch (e) {
      setError(`Siirto epäonnistui: ${e.message}`);
    }
  };

  // Moves a puzzle to a future day, swapping with the puzzle there if there is one.
  const moveTo = (from, to) => {
    if (from === to || !puzzles[from] || !isFuture(from) || !isFuture(to)) return;
    applyMoves(puzzles[to] ? { [from]: to, [to]: from } : { [from]: to });
  };

  const moveByDate = () => {
    const to = idFromInput(moveDate);
    if (!moveDate || !isFuture(to)) { setError('Valitse tuleva päivä'); return; }
    setMoveDate('');
    moveTo(selected, to);
  };

  // Throws the selected puzzle to the next empty day and stays on this day, now free for a new puzzle.
  const throwForward = () => {
    let to = nextDay(selected);
    while (puzzles[to]) to = nextDay(to);
    applyMoves({ [selected]: to }, { stay: true });
  };

  const remove = async () => {
    if (!puzzles[selected] || !window.confirm(`Poistetaanko nono ${selected}?`)) return;
    try {
      await deleteNonoPuzzle(selected);
      const next = { ...puzzles };
      delete next[selected];
      setPuzzles(next);
      setDraft(newDraft());
      resetHistory();
    } catch (e) {
      setError(`Poisto epäonnistui: ${e.message}`);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!draft || tracer || sheet || picker) return;
      if (e.key === 'Escape' && lineTool) { setLineTool(null); return; }
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (mod && key === 'z') { e.preventDefault(); undo(e.shiftKey); return; }
      if (mod && key === 'y') { e.preventDefault(); undo(true); return; }
      if (mod || e.altKey) return;
      const arrows = { arrowleft: [0, -1], arrowright: [0, 1], arrowup: [-1, 0], arrowdown: [1, 0] };
      if (arrows[key]) { e.preventDefault(); shift(...arrows[key]); return; }
      if (e.key === 'Enter') { e.preventDefault(); document.activeElement?.blur(); save(true); return; }
      if (key === 'e' && !locked) setPicker('emoji');
      else if (key === 'k' && !locked) setPicker('icon');
      else if (key === 'b' && !locked) setPicker('onebit');
      else if (key === 'u' && !locked) fileInput.current?.click();
      else if (key === 'r' && !locked) updateActive(g => ({ ...g, cells: randomWarmup(g.width, g.height) }));
      else if (key === 'i' && !locked) updateActive(g => ({ ...g, cells: g.cells.map(v => 1 - v) }));
      else if (key === 'c' && !locked) updateActive(cropped);
      else if (key === 'm') setMirror(m => ({ ...m, x: !m.x }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  useEffect(() => {
    const onPaste = (e) => {
      if (!draft || locked) return;
      const item = [...(e.clipboardData?.items || [])].find(i => i.type.startsWith('image/'));
      if (!item) return;
      e.preventDefault();
      if (sheet) setSheet({ file: item.getAsFile() });
      else openImage(item.getAsFile());
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  });

  if (error && !puzzles) return <div className="tl-error">{error}</div>;
  if (!puzzles) return <div>Ladataan nonoja...</div>;

  const firstEmpty = () => {
    let id = formatDate(new Date());
    while (puzzles[id]) id = nextDay(id);
    return id;
  };

  const phone = grid && analysis ? phoneCell(analysis.clues, grid.width, grid.height) : null;

  return (
    <div className="tl-container nn-container">
      <MonthCalendar month={month} onMonthChange={setMonth} selectedId={selected} renderDay={(id, n) => {
            const p = puzzles[id];
            const future = isFuture(id);
            const info = getDayInfo(parseId(id));
            const kind = info ? (info.isHoliday ? 'holiday' : info.isFlag ? 'flag' : 'notable') : '';
            const cls = ['tl-day', 'nn-day', p ? 'filled' : 'empty', kind ? `nn-${kind}` : '', isToday(id) ? 'today' : '', isPast(id) ? 'past' : '', id === selected ? 'selected' : '', dragId && future && id !== dragId ? 'drop' : ''].join(' ');
            return (
              <button key={id} className={cls} onClick={() => select(id)} title={[info?.title, p?.main?.title].filter(Boolean).join('\n')}
                draggable={future && !!p}
                onDragStart={e => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', id); }}
                onDragEnd={() => setDragId(null)}
                onDragOver={e => { if (dragId && future) e.preventDefault(); }}
                onDrop={e => { e.preventDefault(); const from = dragId; setDragId(null); if (from) moveTo(from, id); }}>
                <span className="tl-day-number">{n}</span>
                {info && <span className="nn-special-dot" />}
                {p?.main && <NonoThumb grid={p.main} size={24} />}
              </button>
            );
          }}>
        <button className="btn-primary tl-first-empty" onClick={() => select(firstEmpty())}>Seuraava tyhjä päivä</button>
        {!touch && <p className="tl-hint">Enter tallentaa ja siirtyy seuraavaan tyhjään päivään. Liitä kuva leikepöydältä Ctrl+V. Vedä tulevan päivän nono toiselle päivälle vaihtaaksesi paikkaa.</p>}      </MonthCalendar>

      <section className="tl-editor nn-editor">
        {!selected || !draft ? (
          <p className="tl-hint">Valitse päivä.</p>
        ) : (
          <>
            <div className="tl-editor-header">
              <h2>{selected}{puzzles[selected] ? '' : ' (uusi)'}{dirty && !locked ? ' •' : ''}</h2>
              {!locked && (
                <div className="tl-actions">
                  {puzzles[selected] && isFuture(selected) && (
                    <div className="nn-move">
                      <input type="date" value={moveDate} min={inputFromId(nextDay(formatDate(new Date())))} onChange={e => setMoveDate(e.target.value)} title="Siirrä tälle päivälle (vaihtaa paikkaa, jos päivällä on nono)" />
                      <button onClick={throwForward} title="Siirrä seuraavalle tyhjälle päivälle ja jää tälle päivälle lisäämään uusi" aria-label="Siirrä seuraavalle tyhjälle päivälle"><UiIcon name="keyboard_double_arrow_right" /></button>
                      <button onClick={moveByDate} disabled={!moveDate}>Siirrä</button>
                    </div>
                  )}
                  {puzzles[selected] && <button className="btn-danger" onClick={remove}>Poista</button>}
                  <button className="btn-secondary" onClick={() => save(false)} disabled={saving || !dirty}>Tallenna</button>
                  <button className="btn-primary" onClick={() => save(true)} disabled={saving}>Tallenna ja seuraava <kbd>Enter</kbd></button>
                </div>
              )}
            </div>

            {(() => {
              const info = getDayInfo(parseId(selected));
              return info && <div className="nn-special-note"><UiIcon name={info.isFlag ? 'flag' : 'celebration'} /> {info.title.split('\n').join(' · ')}</div>;
            })()}
            {error && <div className="tl-error">{error}</div>}
            {locked && <div className="tl-hint">Mennyt nono, vain luku.</div>}
            {repeatOf && <div className="tl-error">Sama kuva oli jo päivänä {repeatOf}.</div>}

            {grid && !locked && !tracer && (
              <div className="nn-toolbar">
                <div className="nn-group nn-group-sizes">
                  {SIZES.map(([w, h]) => (
                    <button key={`${w}x${h}`} className={`nn-chip ${grid.width === w && grid.height === h ? 'active' : ''}`} onClick={() => resize(w, h)}>{w}×{h}</button>
                  ))}
                  <input type="number" className="nn-size" min={MIN_SIZE} max={MAX_SIZE} value={grid.width} title="Leveys" onChange={e => resize(Number(e.target.value), grid.height)} />
                  ×
                  <input type="number" className="nn-size" min={MIN_SIZE} max={MAX_SIZE} value={grid.height} title="Korkeus" onChange={e => resize(grid.width, Number(e.target.value))} />
                </div>
                <div className="nn-group">
                  <button className="nn-tool" onClick={() => fileInput.current?.click()} title="Lataa kuva (U), tai liitä Ctrl+V tai pudota tiedosto"><span className="material-symbols-outlined ui-icon">image</span> Kuva</button>
                  <button className="nn-tool" onClick={() => setPicker('emoji')} title="Emoji (E)"><span className="material-symbols-outlined ui-icon">mood</span> Emoji</button>
                  <button className="nn-tool" onClick={() => setPicker('icon')} title="Material-symboli (K)"><span className="material-symbols-outlined ui-icon">category</span> Symboli</button>
                  <button className="nn-tool" onClick={() => setPicker('onebit')} title="1-bittinen pikselikuva (B)"><span className="material-symbols-outlined ui-icon">grid_on</span> 1-bit</button>
                  <button className="nn-tool" onClick={openSheet} title="Liitä suurennettu pikselikuva ja rajaa siitä osa"><span className="material-symbols-outlined ui-icon">crop_free</span> Pikselikartta</button>
                  <button className="nn-tool" onClick={() => updateActive(g => ({ ...g, cells: randomWarmup(g.width, g.height) }))} title="Satunnainen abstrakti kuvio, joka ratkeaa päättelyllä (R)"><span className="material-symbols-outlined ui-icon">casino</span> Arvo</button>
                  <button className={`nn-tool ${lineTool === 'row' ? 'active' : ''}`} onClick={() => setLineTool(t => (t === 'row' ? null : 'row'))} title="Poista rivi klikkaamalla sitä (Esc lopettaa)"><span className="material-symbols-outlined ui-icon">horizontal_rule</span> Poista rivi</button>
                  <button className={`nn-tool ${lineTool === 'col' ? 'active' : ''}`} onClick={() => setLineTool(t => (t === 'col' ? null : 'col'))} title="Poista sarake klikkaamalla sitä (Esc lopettaa)"><span className="material-symbols-outlined ui-icon nn-rot90">horizontal_rule</span> Poista sarake</button>
                  <button className="nn-tool" onClick={() => updateActive(cropped)} title="Rajaa tyhjät rivit ja sarakkeet reunoilta (C)"><span className="material-symbols-outlined ui-icon">crop</span> Rajaa</button>
                </div>
                <div className="nn-group">
                  <button className={`nn-tool ${erase ? 'active' : ''}`} onClick={() => setErase(v => !v)} aria-pressed={erase} title="Pyyhi: veto tyhjentää ruutuja (oikea painike tekee saman)"><span className="material-symbols-outlined ui-icon">ink_eraser</span> Pyyhi</button>
                  <button className="nn-tool icon" onClick={() => undo(false)} title="Kumoa (Ctrl+Z)"><span className="material-symbols-outlined ui-icon">undo</span></button>
                  <button className="nn-tool icon" onClick={() => undo(true)} title="Tee uudelleen (Ctrl+Y)"><span className="material-symbols-outlined ui-icon">redo</span></button>
                  <button className="nn-tool icon" onClick={() => updateActive(g => ({ ...g, cells: g.cells.map(() => 0) }))} title="Tyhjennä"><span className="material-symbols-outlined ui-icon">delete</span></button>
                  <button className="nn-tool icon" onClick={() => updateActive(g => ({ ...g, cells: g.cells.map(v => 1 - v) }))} title="Käännä värit (I)"><span className="material-symbols-outlined ui-icon">invert_colors</span></button>
                  <button className="nn-tool icon" onClick={() => updateActive(g => transformed(g, (r, c, w) => ({ r, c: w - 1 - c })))} title="Peilaa vaakasuunnassa"><span className="material-symbols-outlined ui-icon">flip</span></button>
                  <button className="nn-tool icon" onClick={() => updateActive(g => transformed(g, (r, c, w, h) => ({ r: h - 1 - r, c })))} title="Peilaa pystysuunnassa"><span className="material-symbols-outlined ui-icon nn-rot90">flip</span></button>
                </div>
                <div className="nn-group">
                  <label className="nn-check" title="Piirrä samalla peilikuva (M)"><input type="checkbox" checked={mirror.x} onChange={e => setMirror(m => ({ ...m, x: e.target.checked }))} /> Peili ↔</label>
                  <label className="nn-check"><input type="checkbox" checked={mirror.y} onChange={e => setMirror(m => ({ ...m, y: e.target.checked }))} /> Peili ↕</label>
                  <label className="nn-check" title="Punaiset ruudut eivät ratkea rivi- ja sarakepäättelyllä"><input type="checkbox" checked={showErrors} onChange={e => setShowErrors(e.target.checked)} /> Näytä ratkeamattomat</label>
                </div>
              </div>
            )}

            <input ref={fileInput} type="file" accept="image/*" hidden
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) openImage(f); }} />

            {grid && (
              <div className="nn-workspace">
                <div ref={areaRef} className="nn-area"
                  onDragOver={e => { if (!locked) e.preventDefault(); }}
                  onDrop={e => {
                    const f = [...(e.dataTransfer?.files || [])].find(x => x.type.startsWith('image/'));
                    if (!f || locked) return;
                    e.preventDefault();
                    if (sheet) setSheet({ file: f });
                    else openImage(f);
                  }}>
                  {sheet ? (
                    <NonoPixelSheet
                      file={sheet.file}
                      onFile={file => setSheet({ file })}
                      onSelect={openPixelIcon}
                      onCancel={() => setSheet(null)}
                    />
                  ) : tracer?.bitmap ? (
                    <NonoPixelTracer
                      width={grid.width}
                      height={grid.height}
                      bitmap={tracer.bitmap}
                      maxWidth={stacked ? areaWidth : areaWidth - 260}
                      onResizeGrid={resize}
                      onApply={cells => { setGrid({ ...grid, cells }); setTracer(null); }}
                      onCancel={() => setTracer(null)}
                    />
                  ) : tracer ? (
                    <NonoTracer
                      width={grid.width}
                      height={grid.height}
                      source={tracer.source}
                      autoContrast={tracer.autoContrast}
                      fillable={tracer.fillable}
                      maxWidth={stacked ? areaWidth : areaWidth - 260}
                      onResizeGrid={resize}
                      onApply={cells => { setGrid({ ...grid, cells }); setTracer(null); }}
                      onCancel={() => setTracer(null)}
                    />
                  ) : (
                    <NonoGridEditor
                      width={grid.width}
                      height={grid.height}
                      cells={grid.cells}
                      analysis={analysis}
                      showErrors={showErrors}
                      mirror={mirror}
                      lineTool={lineTool}
                      erase={erase}
                      onDeleteLine={deleteLine}
                      readOnly={locked}
                      maxWidth={areaWidth}
                      onStrokeStart={record}
                      onChange={cells => setGrid({ ...grid, cells }, { recordHistory: false })}
                    />
                  )}
                </div>

                {!tracer && !sheet && (
                  <aside className="nn-info">
                    <label className="nn-title">
                      Kuvan nimi (näytetään ratkaisun jälkeen)
                      <input value={draft.main.title} disabled={locked} placeholder="esim. Kahvikuppi"
                        onChange={e => setDraft(prev => ({ ...prev, main: { ...prev.main, title: e.target.value } }))}
                        onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
                    </label>
                    <div className={`nn-status nn-status-${analysis.status}`}>
                      <strong>{STATUS_TEXT[analysis.status]}</strong>
                      {analysis.status === 'logic' && <span>Vaikeus: {difficultyText(analysis.passes, grid.width, grid.height)} ({analysis.passes} kierrosta)</span>}
                      {analysis.unresolved > 0 && analysis.status !== 'empty' && <span>{analysis.unresolved} ruutua ei ratkea päättelyllä (punaiset)</span>}
                      {analysis.status === 'multiple' && <span>Lisää tai poista ruutuja punaisella alueella, jotta ratkaisu on yksi.</span>}
                    </div>
                    <dl className="nn-facts">
                      <dt>Täytetty</dt><dd>{analysis.filled} / {grid.width * grid.height} ({Math.round((analysis.filled / (grid.width * grid.height)) * 100)} %)</dd>
                      <dt>Eniten vihjeitä</dt><dd>{analysis.maxClues}</dd>
                      {phone && <><dt>Ruutu puhelimessa</dt><dd className={phone < PHONE_MIN_CELL ? 'warn' : ''}>≈ {phone} px{phone < PHONE_MIN_CELL ? ' (pieni)' : ''}</dd></>}
                    </dl>
                    <div className="nn-preview">
                      <NonoThumb grid={grid} size={120} />
                    </div>
                    <p className="nn-hint">
                      {touch
                        ? 'Napauta tai vedä ruutuja. Pyyhi-työkalulla veto tyhjentää.'
                        : 'Vasen painike vaihtaa, oikea tyhjentää, Shift pitää vedon suorana. Nuolet siirtävät kuvaa. U kuva, E emoji, K symboli, B 1-bit, R arvo, C rajaa, I käännä, M peili.'}
                    </p>
                  </aside>
                )}
              </div>
            )}
          </>
        )}
      </section>

      {picker === 'emoji' && (
        <EmojiPickerDialog onClose={() => setPicker(null)} onSelect={emoji => openTracer(() => sourceFromEmoji(emoji), { fillable: true })} />
      )}
      {picker === 'onebit' && <OneBitPickerDialog onClose={() => setPicker(null)} onSelect={openPixelIcon} />}
      {picker === 'icon' && (
        <IconPickerDialog onClose={() => setPicker(null)} onSelect={name => openTracer(() => sourceFromSymbol(name), { title: titleFromIcon(name), fillable: true })} />
      )}
    </div>
  );
}

export default NonoEditor;
